import { useMemo, useCallback } from 'react';
import { useBookingContext } from 'src/widgets/BookingContext';
import { useLocale } from '@repo/i18n';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    buildActivityContext,
    validateActivityQuantities,
    updateActivityQuantity,
    toActivityInputItems,
} from '@repo/widget-utils/booking/domains/activity/activityQuantities';
import { applyPersonsMaxLimit } from '@repo/widget-utils/booking/domains/shared/capacity-calculators';
import { toDefaultInputItems } from '@repo/widget-utils/booking/domains/shared/transformers';
import {
    mergeWithDefaultItems,
    hasUnavailableItems,
} from '@repo/widget-utils/booking/domains/shared/mergeInputItems';
import type {
    QuantityInputItem,
    QuantityError,
} from '@repo/widget-utils/booking/domains/shared/types';
import { setQuantities } from 'src/state/booking/bookingReducer';

export type UseActivityQuantitiesResult = {
    /** UI-ready input items for the MultipleNumberInput component */
    inputItems: QuantityInputItem[];

    /** Validation errors (empty array if no errors) */
    errors: QuantityError[];

    /** Handler for quantity changes */
    onChangeQuantity: (ticketCategoryId: string, newQuantity: number) => void;

    /** Convenience flag - true if any validation errors exist */
    hasErrors: boolean;

    /** Message to show when some tickets are unavailable for selected date/time */
    disabledMessage?: string;
};

export function useActivityQuantities(): UseActivityQuantitiesResult {
    const {
        quantities,
        defaultTicketOptions,
        selectedProducts,
        selectedProductOption,
        bookingDispatch,
        attemptedBooking,
    } = useBookingContext();
    const { t } = useLocale();
    const config = useConfigurations();

    const context = useMemo(
        () => buildActivityContext(quantities, selectedProducts || []),
        [quantities, selectedProducts],
    );

    const errors = useMemo(
        () => validateActivityQuantities(quantities, context, attemptedBooking),
        [quantities, context, attemptedBooking],
    );

    const availableInputItems = useMemo(() => {
        const items = toActivityInputItems(quantities, context, t, errors);

        if (config.personsMax && config.personsMax > 0) {
            return applyPersonsMaxLimit(items, config.personsMax);
        }

        return items;
    }, [quantities, context, errors, t, config.personsMax]);

    const defaultInputItems = useMemo(() => {
        // Filter by selected product option if one is selected (OCTO only)
        const filteredDefaults = selectedProductOption
            ? defaultTicketOptions.filter(
                  (ticket) => ticket.productOptionId === selectedProductOption.id,
              )
            : defaultTicketOptions;

        return toDefaultInputItems(filteredDefaults, t);
    }, [defaultTicketOptions, selectedProductOption, t]);

    const inputItems = useMemo(() => {
        if (defaultInputItems.length === 0) {
            return availableInputItems;
        }
        return mergeWithDefaultItems(availableInputItems, defaultInputItems);
    }, [availableInputItems, defaultInputItems]);

    const disabledMessage = useMemo(() => {
        if (defaultInputItems.length === 0) return undefined;
        return hasUnavailableItems(inputItems)
            ? t.some_tickets_are_unavailable_the_selected_day
            : undefined;
    }, [inputItems, defaultInputItems.length, t]);

    const onChangeQuantity = useCallback(
        (ticketCategoryId: string, newQuantity: number) => {
            const updatedQuantities = updateActivityQuantity(
                quantities,
                ticketCategoryId,
                newQuantity,
            );
            bookingDispatch(setQuantities(updatedQuantities));
        },
        [quantities, bookingDispatch],
    );

    const hasErrors = errors.length > 0;

    return {
        inputItems,
        errors,
        onChangeQuantity,
        hasErrors,
        disabledMessage,
    };
}
